/**
 * Shared time-of-day types for the day/night cycle system (DON-599).
 *
 * One in-game day = DAY_DURATION_MS (20 real minutes).
 * The authoritative day clock lives on the server; clients interpolate.
 * All timing is deterministic — never coupled to frame rate (§1.8).
 */
/** Total real-time duration of one in-game day, in milliseconds. */
export const DAY_DURATION_MS = 20 * 60 * 1000; // 1 200 000 ms
/** Fraction boundaries for each time-of-day phase (0..1 across the day). */
export const TOD_BOUNDARIES = {
    DAWN_START: 0.00, // 00:00 → 03:00 in-game
    DAY_START: 0.15, // 03:00 → 13:00
    DUSK_START: 0.65, // 13:00 → 16:00
    NIGHT_START: 0.80, // 16:00 → 20:00 (wraps)
};
export const TOD_AMBIENT = {
    dawn: { r: 255, g: 200, b: 140, intensity: 0.55 },
    day: { r: 255, g: 248, b: 220, intensity: 1.00 },
    dusk: { r: 255, g: 140, b: 80, intensity: 0.65 },
    night: { r: 40, g: 60, b: 140, intensity: 0.25 },
};
/**
 * Normalised time position within the current day, in [0, 1).
 * Derived from `dayTimeMs / DAY_DURATION_MS`.
 */
export function getDayFraction(dayTimeMs) {
    return (dayTimeMs % DAY_DURATION_MS) / DAY_DURATION_MS;
}
/** Returns the current TimeOfDay phase for a given day fraction. */
export function getTimeOfDay(fraction) {
    const f = ((fraction % 1) + 1) % 1; // clamp to [0,1)
    if (f < TOD_BOUNDARIES.DAY_START)
        return 'dawn';
    if (f < TOD_BOUNDARIES.DUSK_START)
        return 'day';
    if (f < TOD_BOUNDARIES.NIGHT_START)
        return 'dusk';
    return 'night';
}
/**
 * Linearly interpolates ambient light between two time-of-day phases.
 * `t` is 0..1 within the *current* phase window.
 */
export function lerpAmbient(from, to, t) {
    const lerp = (a, b) => a + (b - a) * t;
    return {
        r: Math.round(lerp(from.r, to.r)),
        g: Math.round(lerp(from.g, to.g)),
        b: Math.round(lerp(from.b, to.b)),
        intensity: lerp(from.intensity, to.intensity),
    };
}
/** Returns how far (0..1) we are through the current phase. */
export function getPhaseProgress(fraction) {
    const f = ((fraction % 1) + 1) % 1;
    const { DAWN_START, DAY_START, DUSK_START, NIGHT_START } = TOD_BOUNDARIES;
    if (f < DAY_START)
        return (f - DAWN_START) / (DAY_START - DAWN_START);
    if (f < DUSK_START)
        return (f - DAY_START) / (DUSK_START - DAY_START);
    if (f < NIGHT_START)
        return (f - DUSK_START) / (NIGHT_START - DUSK_START);
    return (f - NIGHT_START) / (1.0 - NIGHT_START);
}
// ── Lunar cycle (B-027) ───────────────────────────────────────────────────────
//
// GDD §7.5: "the dimming star … dims 1 % per in-world full moon." The GDD does
// not pin a lunar period; this constant fixes one in canonical shared code so
// client and server agree.
//
// 28 in-world days per cycle was chosen as a faithful real-world analogue
// (Earth's synodic month is ~29.5 days). With DAY_DURATION_MS = 20 real
// minutes, one in-world full moon = 28 × 20 = 560 real minutes ≈ 9 h 20 min.
// At 1 % dim per full moon, dimming reaches 100 % in ~38.9 real days — closer
// to "weeks" than the GDD's "years," but a future balance pass owns the
// number; the contract this module guarantees is the *trigger*, not the rate.
/** In-world days per lunar cycle. New moon → full moon → new moon. */
export const LUNAR_DAYS_PER_CYCLE = 28;
/** Real-time ms in one full lunar cycle. */
export const LUNAR_CYCLE_MS = LUNAR_DAYS_PER_CYCLE * DAY_DURATION_MS;
/**
 * Phase offset (0..1) within the lunar cycle at which the moon is "full."
 * Anchored at 0.5 so that day 0 of the world is a new moon and day 14 is
 * full — a phase-0 anchor would put a full moon at world creation, which is
 * unreasonable for a fresh-install dimming counter.
 */
export const FULL_MOON_PHASE = 0.5;
/**
 * Returns the moon phase in [0, 1) for an absolute in-world ms timestamp.
 *   0.0  → new moon
 *   0.25 → first quarter
 *   0.5  → full moon
 *   0.75 → last quarter
 *
 * `absoluteWorldMs` is unbounded — unlike `dayTimeMs` it does NOT wrap at the
 * end of an in-world day. Call sites that only know `dayTimeMs` (which loops
 * every 20 real minutes) cannot derive lunar phase on their own; they must
 * track an absolute counter or substitute wall-clock ms (the trade-off is
 * lunar period drifts with server uptime, but that's acceptable for a
 * once-per-9-hour trigger).
 */
export function getMoonPhase(absoluteWorldMs) {
    const m = absoluteWorldMs % LUNAR_CYCLE_MS;
    return ((m + LUNAR_CYCLE_MS) % LUNAR_CYCLE_MS) / LUNAR_CYCLE_MS;
}
/** True if the moon phase at this timestamp falls within the full-moon "day." */
export function isFullMoon(absoluteWorldMs) {
    // A full moon "day" is one in-world day wide, centred on FULL_MOON_PHASE.
    const halfWindow = (DAY_DURATION_MS / LUNAR_CYCLE_MS) / 2;
    const phase = getMoonPhase(absoluteWorldMs);
    return Math.abs(phase - FULL_MOON_PHASE) < halfWindow;
}
/**
 * Counts how many full-moon crossings occurred in the half-open interval
 * `(prevMs, currMs]`. Returns 0 if `currMs <= prevMs`.
 *
 * Crossing semantics: a full moon is "crossed" when the cycle index advances,
 * i.e. when `floor((t - fullMoonOffset) / LUNAR_CYCLE_MS)` increments. This
 * gives exactly one crossing per cycle regardless of polling cadence.
 */
export function getFullMoonsBetween(prevMs, currMs) {
    if (currMs <= prevMs)
        return 0;
    const offset = FULL_MOON_PHASE * LUNAR_CYCLE_MS;
    const prevIdx = Math.floor((prevMs - offset) / LUNAR_CYCLE_MS);
    const currIdx = Math.floor((currMs - offset) / LUNAR_CYCLE_MS);
    return Math.max(0, currIdx - prevIdx);
}
//# sourceMappingURL=time.js.map